const THUMBNAIL_PREVIEW_STORAGE_PREFIX = 'printboard:thumbnail-preview:'
const THUMBNAIL_PREVIEW_CHANNEL = 'printboard-board-thumbnail-preview'
const THUMBNAIL_PREVIEW_TTL_MS = 5 * 60 * 1000

type ThumbnailPreviewMessage =
  | {
      type: 'set'
      userId: string
      boardId: string
      previewUrl: string
      timestamp: number
    }
  | {
      type: 'clear'
      userId: string
      boardId: string
      persistedThumbnailUrl?: string | null
      timestamp: number
    }

export interface BoardThumbnailPreviewEntry {
  boardId: string
  previewUrl: string
  timestamp: number
}

const isBrowser = typeof window !== 'undefined'

const getStorageKey = (userId: string, boardId: string) =>
  `${THUMBNAIL_PREVIEW_STORAGE_PREFIX}${userId}:${boardId}`

const isFresh = (timestamp: number) =>
  Date.now() - timestamp < THUMBNAIL_PREVIEW_TTL_MS

const parseMessage = (value: string | null): ThumbnailPreviewMessage | null => {
  if (!value) return null

  try {
    const parsed = JSON.parse(value) as ThumbnailPreviewMessage
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof parsed.userId !== 'string' ||
      typeof parsed.boardId !== 'string' ||
      typeof parsed.timestamp !== 'number'
    ) {
      return null
    }

    if (!isFresh(parsed.timestamp)) {
      return null
    }

    if (parsed.type === 'set' && typeof parsed.previewUrl === 'string') {
      return parsed
    }

    if (parsed.type === 'clear') {
      return parsed
    }
  } catch {
    return null
  }

  return null
}

const writeMessageToStorage = (message: ThumbnailPreviewMessage) => {
  if (!isBrowser) return

  const storageKey = getStorageKey(message.userId, message.boardId)

  try {
    if (message.type === 'clear') {
      window.localStorage.removeItem(storageKey)
      return
    }

    window.localStorage.setItem(storageKey, JSON.stringify(message))
  } catch {
    // Ignore storage failures and rely on in-memory delivery in the current tab.
  }
}

const createChannel = () => {
  if (!isBrowser || typeof BroadcastChannel === 'undefined') return null
  return new BroadcastChannel(THUMBNAIL_PREVIEW_CHANNEL)
}

const broadcastMessage = (message: ThumbnailPreviewMessage) => {
  const channel = createChannel()
  if (!channel) return

  channel.postMessage(message)
  channel.close()
}

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }

      reject(new Error('Failed to convert thumbnail blob to data URL'))
    }
    reader.onerror = () =>
      reject(reader.error || new Error('Failed to read thumbnail blob'))
    reader.readAsDataURL(blob)
  })

export const publishBoardThumbnailPreview = async ({
  userId,
  boardId,
  blob,
}: {
  userId: string
  boardId: string
  blob: Blob
}) => {
  if (!isBrowser) return

  const previewUrl = await blobToDataUrl(blob)
  publishBoardThumbnailPreviewUrl({
    userId,
    boardId,
    previewUrl,
  })
}

export const publishBoardThumbnailPreviewUrl = ({
  userId,
  boardId,
  previewUrl,
}: {
  userId: string
  boardId: string
  previewUrl: string
}) => {
  if (!isBrowser) return

  const message: ThumbnailPreviewMessage = {
    type: 'set',
    userId,
    boardId,
    previewUrl,
    timestamp: Date.now(),
  }

  writeMessageToStorage(message)
  broadcastMessage(message)
}

export const clearBoardThumbnailPreview = ({
  userId,
  boardId,
  persistedThumbnailUrl,
}: {
  userId: string
  boardId: string
  persistedThumbnailUrl?: string | null
}) => {
  const message: ThumbnailPreviewMessage = {
    type: 'clear',
    userId,
    boardId,
    persistedThumbnailUrl,
    timestamp: Date.now(),
  }

  writeMessageToStorage(message)
  broadcastMessage(message)
}

export const readBoardThumbnailPreviews = (
  userId: string
): Record<string, BoardThumbnailPreviewEntry> => {
  if (!isBrowser) return {}

  const entries: Record<string, BoardThumbnailPreviewEntry> = {}
  const prefix = `${THUMBNAIL_PREVIEW_STORAGE_PREFIX}${userId}:`

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key || !key.startsWith(prefix)) continue

    const message = parseMessage(window.localStorage.getItem(key))
    if (!message) {
      window.localStorage.removeItem(key)
      continue
    }

    if (message.type !== 'set') continue

    entries[message.boardId] = {
      boardId: message.boardId,
      previewUrl: message.previewUrl,
      timestamp: message.timestamp,
    }
  }

  return entries
}

export const subscribeToBoardThumbnailPreviews = ({
  userId,
  onSet,
  onClear,
}: {
  userId: string
  onSet: (entry: BoardThumbnailPreviewEntry) => void
  onClear: (payload: {
    boardId: string
    persistedThumbnailUrl?: string | null
  }) => void
}) => {
  if (!isBrowser) {
    return () => undefined
  }

  const handleMessage = (message: ThumbnailPreviewMessage | null) => {
    if (!message || message.userId !== userId) return

    if (message.type === 'set') {
      onSet({
        boardId: message.boardId,
        previewUrl: message.previewUrl,
        timestamp: message.timestamp,
      })
      return
    }

    onClear({
      boardId: message.boardId,
      persistedThumbnailUrl: message.persistedThumbnailUrl,
    })
  }

  const channel = createChannel()
  if (channel) {
    channel.onmessage = (event: MessageEvent<ThumbnailPreviewMessage>) => {
      handleMessage(event.data)
    }
  }

  const handleStorage = (event: StorageEvent) => {
    if (!event.key || !event.key.startsWith(THUMBNAIL_PREVIEW_STORAGE_PREFIX)) {
      return
    }

    handleMessage(parseMessage(event.newValue))

    if (event.newValue === null) {
      const [storageUserId, boardId] = event.key
        .slice(THUMBNAIL_PREVIEW_STORAGE_PREFIX.length)
        .split(':')
      if (storageUserId === userId && boardId) {
        onClear({ boardId })
      }
    }
  }

  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener('storage', handleStorage)
    channel?.close()
  }
}
